import React, {useEffect, useRef, useState} from "react"
import {ExternalLinkButton, FormGroup, FormGroups, NumericInput} from "./components/FormComps";
import {clearError, InfoBlock, InfoText} from "./components/ValidationUtils";
import {useIntlEx} from "./components/IntlUtils";
import {useSolv} from "./components/SolvProvider";
import {ImageDropBox, processImageFile} from "./components/ImageUtils";
import ImageDialog from "./components/ImageDialog";
import {AsyncPaginate} from "react-select-async-paginate";
import {default as Select} from "react-select";
import {numberIsNotBlank} from "./components/Utils";
import {useClientSupportLevelOpts, useResellerType, useTaxType} from "./components/AccountUtils";
import {mkExtraParams} from "./components/TableComps";
import useStateRef from "react-usestateref";
import ToggleButton from "./components/ToggleButton";
import TagInput from "./components/TagInput";
import {rangeMap} from "./components/CommonUtils";
import {booleanProp} from "./components/ReactUtils";
import getLogger from "./components/Logging";
const log = getLogger("AccountCoreComps")

export function AccountCoreComps({data, update, required, hidden}) {

  const {session} = useSolv()
  const {intl} = useIntlEx()

  function handleOwnerEmailAddressChange(e) {
    update({path: "ownerEmailAddress", value: e.target.value})
  }

  function handleTenantNameChange(e) {
    update({path: "tenantName", value: e.target.value})
  }

  return (
    <>
      <FormGroups id="sec_account" hidden={hidden?.sec_account}>
        <FormGroup hidden={hidden?.inp_ownerEmailAddress}>
          <FormGroup.Label text={intl.msg("account_invite_email_address")} description={intl.msg("account_invite_email_address_description")} htmlFor="inp_emailAddress" autoFocus={true}/>
          <FormGroup.Control>
            <input key="inp_ownerEmailAddress" id="inp_ownerEmailAddress" className="form-control" type="text" value={data?.ownerEmailAddress} onChange={handleOwnerEmailAddressChange}/>
            <InfoBlock>
              <InfoText validate="valid">{intl.msg("helptext_email_address_valid")}</InfoText>
              <InfoText validate="unique">{intl.msg("helptext_email_address_unique")}</InfoText>
              <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
            </InfoBlock>
          </FormGroup.Control>
        </FormGroup>

        <FormGroup>
          <FormGroup.Label htmlFor="inp_tenantName" text={intl.msg("account_name")} description={intl.msg("account_name_description")}/>
          <FormGroup.Controls>
            <input id="inp_tenantName" type="text" className="form-control" value={data?.tenantName} maxLength="200" onChange={handleTenantNameChange} autoComplete="off" autoFocus={hidden?.inp_ownerEmailAddress}/>
            <InfoBlock>
              <InfoText>{intl.msg("helptext_name_casing")}</InfoText>
              <InfoText validate="unique">{intl.msg("account_name_helptext_unique")}</InfoText>
              <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
            </InfoBlock>
         </FormGroup.Controls>
        </FormGroup>
      </FormGroups>

    </>
  )
}

export function AccountContactAndBillingComps({data, update, required, collapsed, hidden, disabled, ...props}) {

  const {session, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const [logoImageInfo, setLogoImageInfo] = useState(null)
  const [billingNameEntangled, setBillingNameEntangled] = useState(false)
  const [billingAddressEntangled, setBillingAddressEntangled] = useState(false)

  const logoImagePickerDialogRef = useRef(null)

  useEffect(() => {
    if (data) {
      setBillingNameEntangled(!data.billingInfo?.name)
      setBillingAddressEntangled(!data.billingInfo?.address)
    }
  }, [])

  function handleDisplayNameChange(e) {
    update({path: "displayName", value: e.target.value})
    if (billingNameEntangled) {
      update({path: "billingInfo.name", value: e.target.value})
    }
  }

  function handleContactInfoChange(e) {
    update({path: "contactInfo", value: e.target.value})
    if (billingAddressEntangled) {
      update({path: "billingInfo.address", value: e.target.value})
    }
  }

  function setLogoUrl(v) {
    update({path: "logoUrl", value: v})
  }

  function setTouched() {
    update({touched: true})
  }

  async function handleLogoChange(e) {

    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0]
      let result

      setBusy(intl.msg("processing"))

      try {
        result = await processImageFile({
          file: file,
          maxFileSize: 100_000,
          width: 100,
          height: 100,
        })
      }
      catch (ex) {
        update({alert:{type: "error", text: "error_image_process_failed"}})
      }
      finally {
        setBusy(null)
      }

      if (result.warning || result.error) {
        setLogoImageInfo(result)
        const el = document.getElementById("inp_dlg_logoImagePicker")
        el.click()
      }
      else {
        setLogoUrl(result.dataUrl)
      }

      clearError("inp_logoUrl")
    }
  }

  function handleLogoClear() {
    setLogoUrl(null)
  }

  function handleHomePageChange(e) {
    update({path: "homePage", value: e.target.value})
  }

  function handleHomePageBlur() {
    if (data?.homePage?.trim()?.length > 0) {
      try {
        if (data.homePage.startsWith("http://")) {
          update({path: "homePage", value: `https://${data.homePage.substring(7)}`})
        }
        else if (!data.homePage.startsWith("https://")) {
          update({path: "homePage", value: `https://${data.homePage}`})
        }
      }
      catch (e) {
      }
    }
  }

  function handleBillingNameChange(e) {
    update({path: "billingInfo.name", value: e.target.value})
    setBillingNameEntangled(false)
    setTouched(true)
  }

  function handleBillingAddressChange(e) {
    update({path: "billingInfo.address", value: e.target.value})
    setBillingAddressEntangled(false)
    setTouched(true)
  }

  return (
    !booleanProp(props, "disabled") &&
      <>
        <FormGroups id="sec_contact" title={intl.msg("account_contact_section_title", {hasBilling: !hidden?.sec_billing})} description={intl.msg("account_contact_section_description", {hasBilling: !hidden?.sec_billing})} collapsed={collapsed} hidden={hidden?.sec_contact}>

          <FormGroup>
            <FormGroup.Label htmlFor="inp_displayName" text={intl.msg("account_contact_name")} description={intl.msg("account_contact_name_description")}/>
            <FormGroup.Controls>
              <input id="inp_displayName" type="text" className="form-control" value={data?.displayName} onChange={handleDisplayNameChange} autoComplete="off"/>
              <InfoBlock>
                <InfoText validate={required?.inp_displayName ? "required" : "optional"}>{intl.msg(`helptext_${required?.inp_displayName  ? "required" : "optional"}`)}</InfoText>
              </InfoBlock>
            </FormGroup.Controls>
          </FormGroup>

          <FormGroup>
            <FormGroup.Label htmlFor="inp_contactInfo" text={intl.msg("account_contact_address")} description={intl.msg("account_contact_address_description")}/>
            <FormGroup.Controls>
              <textarea id="inp_contactInfo" className="form-control" rows="3" autoComplete="off" value={data?.contactInfo} onChange={handleContactInfoChange} style={{whiteSpace: "pre-line"}}/>
              <InfoBlock>
                <InfoText validate={required?.inp_contactInfo ? "required" : "optional"}>{intl.msg(`helptext_${required?.inp_contactInfo ? "required" : "optional"}`)}</InfoText>
              </InfoBlock>
            </FormGroup.Controls>
          </FormGroup>

          <FormGroup>
            <FormGroup.Label htmlFor="inp_logoUrl" text={intl.msg("account_logo")} description={intl.msg("account_logo_description")}/>
            <FormGroup.Controls>
              <ImageDropBox
                id="inp_logoUrl"
                image={data?.logoUrl}
                imageClassName={"account-logo"}
                width={100}
                height={100}
                onChange={handleLogoChange}
                onClear={handleLogoClear}
              />
              <InfoBlock>
                <InfoText validate="valid_size">{intl.msg("account_logo_helptext_valid_size")}</InfoText>
                <InfoText validate={required?.inp_logoUrl ? "required" : "optional"}>{intl.msg(`helptext_${required?.inp_logoUrl ? "required" : "optional"}`)}</InfoText>
              </InfoBlock>
            </FormGroup.Controls>
          </FormGroup>

          <FormGroup>
            <FormGroup.Label htmlFor="inp_homePage" text={intl.msg("account_home_page")} description={intl.msg("account_home_page_description")}/>
            <FormGroup.Controls>
              <div className="input-group" style={{gap: "0.5rem"}}>
                <input id="inp_homePage" type="text" className="form-control" value={data?.homePage} onChange={handleHomePageChange} onBlur={handleHomePageBlur}/>
                <div className="input-group-append">
                  <ExternalLinkButton href={data?.homePage} validate="relaxed"/>
                </div>
              </div>
              <InfoBlock>
                <InfoText validate="valid">{intl.msg("helptext_web_address_valid")}</InfoText>
                <InfoText validate={required?.inp_homePage? "required" : "optional"}>{intl.msg(`helptext_${required?.inp_homePage ? "required" : "optional"}`)}</InfoText>
              </InfoBlock>
            </FormGroup.Controls>
          </FormGroup>

          <FormGroup hidden={hidden?.sec_billing}>
            <FormGroup.Label htmlFor="inp_billingName" text={intl.msg("account_billing_name")} description={intl.msg("account_billing_name_description")}/>
            <FormGroup.Controls>
              <input id="inp_billingName" type="text" className="form-control" value={data?.billingInfo?.name} onChange={handleBillingNameChange} autoComplete="off"/>
              <InfoBlock>
                <InfoText validate={required?.inp_billingName ? "required" : "optional"}>{intl.msg(`helptext_${required?.inp_billingName ? "required" : "optional"}`)}</InfoText>
              </InfoBlock>
            </FormGroup.Controls>
          </FormGroup>

          <FormGroup hidden={hidden?.sec_billing}>
            <FormGroup.Label htmlFor="inp_billingAddress" text={intl.msg("account_billing_address")} description={intl.msg("account_billing_address_description")}/>
            <FormGroup.Controls>
              <textarea id="inp_billingAddress" className="form-control" rows="3" autoComplete="off" value={data?.billingInfo?.address} onChange={handleBillingAddressChange} style={{whiteSpace: "pre-line"}}/>
              <InfoBlock>
                <InfoText validate={required?.inp_billingAddress ? "required" : "optional"}>{intl.msg(`helptext_${required?.inp_billingAddress ? "required" : "optional"}`)}</InfoText>
              </InfoBlock>
            </FormGroup.Controls>
          </FormGroup>

        </FormGroups>

        <ImageDialog ref={logoImagePickerDialogRef} id="dlg_logoImagePicker" title="Logo Image" imageInfo={logoImageInfo} setTargetImageData={setLogoUrl} setTouched={setTouched}/>

      </>
  )
}

export function AccountResellerComps({data, prev, update, required, hidden}) {

  const {intl} = useIntlEx()

  // const {industryOpts, findIndustryOpt} = useIndustry()
  const {getResellerType, getChangeableResellerTypeOpts, findResellerTypeOpt} = useResellerType()
  const {taxTypeOpts, findTaxTypeOpt} = useTaxType()
  const {clientSupportLevelOpts, findClientSupportLevelOptByLevel} = useClientSupportLevelOpts()

  const [resellerType, setResellerType] = useState(undefined)
  const [clientSupportLevel, setClientSupportLevel] = useState(null)
  const [ttlCommissionRates, setTtlCommissionRates] = useState(null)

  useEffect(() => {
    console.log(">>> AccountResellerComps: useEffect[data?.commissionRates, data?.maxDepth]: ", data)
    setTtlCommissionRates(Array.isArray(data?.commissionRates) && data?.commissionRates?.length > 0 ? data?.commissionRates.slice(0, (data?.maxDepth + 1)).reduce((acc, cur) => cur ? acc + cur : acc) : 0.0)
  }, [data?.commissionRates, data?.maxDepth])

  useEffect(() => {
  }, [ttlCommissionRates])

  useEffect(() => {
    if (data && (resellerType === undefined)) {
      setResellerType(findResellerTypeOpt(getResellerType(data)))
    }
  }, [data])

  function handleResellerTypeChange(v) {
    update({path: "resellerType", value: v.value})
    setResellerType(v)
  }

  function handleMaxDepthChange(v) {
    // console.log(">>>Z: handleMaxDepthChange: v=", (v && v > 0 ? v - 1 : 0))
    update({path: "maxDepth", value: (v && v > 0 ? v - 1 : 0)})
  }

  function handleCommissionRatesChange(i, v) {
    update({path: `commissionRates[${i}]`, value: v})
  }

  function handleTaxRateChange(v) {
    update({path: "taxRate", value: v})
  }

  function handleTaxTypeChange(v) {
    update({path: "taxType", value: v?.value})
  }

  function handleClientSupportLevelChange(v) {
    if (v.level !== null) {
      update({path: "supportLevel", value: v.level})
    }
  }

  return (

    <FormGroups id="sec_reseller" title={intl.msg("account_reseller_settings")} description={intl.msg("account_reseller_settings_description")} hidden={hidden?.sec_reseller}>

      <FormGroup>
        <FormGroup.Label htmlFor="inp_resellerType" text={intl.msg("reseller_type")} description={intl.msg("reseller_type_description")}/>
        <FormGroup.Controls>
          <div style={{width: "200px"}}>
            <Select
              id="inp_resellerType"
              className="react-select"
              classNamePrefix="react-select"
              value={resellerType || findResellerTypeOpt(getResellerType(data))}
              options={getChangeableResellerTypeOpts(data)}
              menuPortalTarget={document.body}
              hideSelectedOptions={true}
              isSearchable={false}
              placeholder={null}
              onChange={handleResellerTypeChange}
            />
          </div>
          <InfoBlock>
            <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
            {/*{*/}
            {/*  resellerType?.value === "STANDALONE" && getResellerType(data) !== "STANDALONE" &&*/}
            {/*    <InfoText warning={true}>{intl.msg(`reseller_type_helptext_${getResellerType(data).toLowerCase()}_to_${resellerType?.value.toLowerCase()}_warning`)}</InfoText>*/}
            {/*}*/}
          </InfoBlock>
        </FormGroup.Controls>
      </FormGroup>

      {
        "MASTER" === resellerType?.value? (
          <>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_maxLevels" text={intl.msg("reseller_max_levels")} description={intl.msg("reseller_max_levels_description")}/>
              <FormGroup.Controls>
                <div style={{width: "50px"}}>
                  <NumericInput id="inp_maxDepth" className="form-control" strict={true} min={2} max={5} decimals={0} value={data?.maxDepth + 1} onChange={handleMaxDepthChange}/>
                </div>
                <InfoBlock>
                  <InfoText validate="valid">{intl.msg("reseller_max_levels_helptext_valid", {minValue: 2, maxValue: 5})}</InfoText>
                  {/*<InfoText validate="valid">{intl.msg("reseller_max_levels_helptext_valid", {minValue: data?.commissionRates && data?.commissionRates?.length > 0 ? data?.commissionRates?.length : 2, maxValue: 5})}</InfoText>*/}
                  {
                    data?.commissionRates && (data?.maxDepth + 1) < prev?.commissionRates.length &&
                      <InfoText warning={true}>{intl.msg("reseller_max_levels_helptext_reduce_warning", {minValue: data?.commissionRates && data?.commissionRates?.length > 0 ? data?.commissionRates?.length : 2, maxValue: 5})}</InfoText>
                  }
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            {
              (data?.maxDepth + 1) > 0 &&
              <>
                <FormGroup>
                  <FormGroup.Label htmlFor="inp_commissions" text={intl.msg("reseller_commission_rates")} description={intl.msg("reseller_commission_rates_description")}/>
                  <FormGroup.Controls>
                    <table border="0">
                      {
                        rangeMap(0, data?.maxDepth, (i) => {
                          return (
                            <tr>
                              <td>
                                <label className="col-form-label pr-2" htmlFor={`inp_commission_${i}`}>
                                  <span>
                                    {
                                      i === 0 ? (
                                        intl.msg("reseller_type_master_l")
                                      ) : i >= data?.maxDepth ? (
                                        intl.msg("reseller_type_sub_l", {subLevel: i})
                                      ) : (
                                        intl.msg("reseller_type_sub_l", {subLevel: i})
                                      )
                                    }:
                                  </span>
                                </label>
                              </td>
                              <td>
                                <div className="form-control-wrapper">
                                  <div style={{width: "80px"}}>
                                    <NumericInput id={`inp_commission_${i}`} className="form-control" min={1.00} max={99.00} decimals={2} value={data?.commissionRates && data?.commissionRates?.length > 0 ? data?.commissionRates[i] : 0.00} onChange={(v) => handleCommissionRatesChange(i, v)}/>
                                  </div>
                                  <div className="form-control-suffix">%</div>
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      }

                      <tr>
                        <td>
                          <label className="col-form-label pr-2" htmlFor="inp_commission_standalone">
                            {intl.msg("total")}:
                          </label>
                        </td>
                        <td>
                          <div className="form-control-wrapper">
                            <div style={{width: "80px"}}>
                              <NumericInput id={`inp_commission_total`} key={`inp_commission_total_${ttlCommissionRates}`} className="form-control" min={1.00} max={90.00} decimals={2} disabled={true} value={ttlCommissionRates}/>
                            </div>
                            <div className="form-control-suffix">%</div>
                          </div>
                        </td>
                      </tr>

                      {
                        data?.commissionRates && (data?.maxDepth + 1) < prev?.commissionRates.length &&
                        <>
                          <tr>
                            <td colspan="2" style={{height: "12px"}}>

                            </td>
                          </tr>
                          <tr>
                            <td>
                              <label className="col-form-label pr-2" htmlFor="inp_commission_standalone">
                                {intl.msg("reseller_type_standalone")}:
                              </label>
                            </td>
                            <td>
                              <div className="form-control-wrapper">
                                <div style={{width: "80px"}}>
                                  <NumericInput id={`inp_commission_standalone`} className="form-control" min={1.00} max={90.00} decimals={2} value={data?.commissionRates?.length > data?.maxDepth? data?.commissionRates?.[data?.maxDepth + 1] : 0.00} onChange={(v) => handleCommissionRatesChange(data?.maxLevels, v)}/>
                                </div>
                                <div className="form-control-suffix">%</div>
                              </div>
                            </td>
                          </tr>
                        </>
                      }

                    </table>
                    <InfoBlock>
                      <InfoText validate="valid">{intl.msg("reseller_commission_rates_helptext_valid")}</InfoText>
                      <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Controls>

                </FormGroup>
              </>
            }
          </>
        ) : "STANDALONE" === resellerType?.value? (
          <>
            <FormGroup>
              <FormGroup.Label htmlFor="inp_commission" text={intl.msg("reseller_commission_rate")} description={intl.msg("reseller_commission_rate_description")}/>
              <FormGroup.Controls>
                <div className="form-control-wrapper">
                  <div style={{width: "80px"}}>
                    {/*<NumericInput id="inp_commission" className="form-control" min={1.00} max={99.00} decimals={2} value={commissionRates && commissionRates.length > 0 ? commissionRates[0] : 0.00} onChange={(v) => handleCommissionRatesChange(0, v)}/>*/}
                    <NumericInput id="inp_commission" className="form-control" min={1.0} max={90.00} decimals={2} value={data?.commissionRates && data?.commissionRates?.length > 0 ? data?.commissionRates?.[0] : 0.00} onChange={(v) => handleCommissionRatesChange(0, v)}/>
                  </div>
                  <div className="form-control-suffix">%</div>
                </div>
                <InfoBlock>
                  <InfoText validate="valid">{intl.msg("reseller_commission_rate_helptext_valid")}</InfoText>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>
          </>
        ) : (
          <></>
        )
      }

      <FormGroup hidden={["DEMO", "HOUSE", "TEST"].includes(resellerType?.value)}>
        <FormGroup.Label text={intl.msg("account_tax")} description={intl.msg("account_tax_description")} htmlFor="inp_taxRate"/>
        <FormGroup.Control>
          <div className="d-flex flex-row gap-1">
            <div className="form-control-wrapper flex-row">
              <div style={{width: "80px"}}>
                <NumericInput id="inp_taxRate" className="form-control" min={0} max={99} decimals={2} value={data?.taxRate} onChange={handleTaxRateChange}/>
              </div>
              <div className="form-control-suffix">%</div>
              {/*<div className="form-control-suffix">{numberIsNotBlank(data?.taxRate)}</div>*/}
            </div>
            <div style={{width: "150px"}}>
              {
                numberIsNotBlank(data?.taxRate) ? (
                  <Select
                    id="inp_taxType"
                    className="react-select"
                    classNamePrefix="react-select"
                    value={findTaxTypeOpt(data?.taxType)}
                    options={taxTypeOpts}
                    menuPortalTarget={document.body}
                    hideSelectedOptions={true}
                    isSearchable={false}
                    placeholder={null}
                    onChange={handleTaxTypeChange}
                  />
                ) : (
                  <></>
                )
              }
            </div>
          </div>
          <InfoBlock>
            <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
          </InfoBlock>
        </FormGroup.Control>
      </FormGroup>

      <FormGroup hidden={!["MASTER", "STANDALONE"].includes(resellerType?.value)}>
        <FormGroup.Label htmlFor="inp_supportLevel" text={intl.msg("client_support_level")} description={intl.msg("client_support_level_description")}/>
        <FormGroup.Controls>
          <div style={{width: "400px"}}>
            <Select
              id="inp_supportLevel"
              className="react-select"
              classNamePrefix="react-select"
              value={findClientSupportLevelOptByLevel(data?.supportLevel)}
              options={clientSupportLevelOpts}
              menuPortalTarget={document.body}
              hideSelectedOptions={true}
              isSearchable={false}
              placeholder={null}
              onChange={handleClientSupportLevelChange}
            />
          </div>
          <InfoBlock>
            <InfoText validate="required">{intl.msg("helptext_optional")}</InfoText>
          </InfoBlock>
        </FormGroup.Controls>
      </FormGroup>

    </FormGroups>
  )
}

export function AccountClientSettingsComps({data, update, hidden}) {

  const {intl} = useIntlEx()

  const [restrictDomains, setRestrictDomains] = useState(undefined)

  useEffect(() => {
    if (data) {
      setRestrictDomains(data.allowedDomains?.length > 0 || data.disallowedDomains?.length > 0)
    }
  }, [data])

  function handleRestrictDomainsChange(v) {
    if (!v) {
      update({path: "allowedDomains", value: []})
      update({path: "disallowedDomains", value: []})
    }
    setRestrictDomains(v)
  }

  function handleAllowedDomainsChange(v) {
    update({path: "allowedDomains", value: v.map(d => d.value)})
  }

  function handleDisallowedDomainsChange(v) {
    update({path: "disallowedDomains", value: v.map(d => d.value)})
  }

  return (
    <FormGroups id="sec_client_misc" text={intl.msg("account_client_misc_settings")} description={intl.msg("account_client_misc_settings_description")} hidden={hidden?.sec_client_misc} collapsed={true}>

      <FormGroup>
        <FormGroup.Label htmlFor="inp_restrictDomains" text={intl.msg("account_restrict_domains")} description={intl.msg("account_restrict_domains_description")}/>
        <FormGroup.Controls>
          <ToggleButton
            id="inp_restrictDomains"
            checked={restrictDomains}
            onChange={handleRestrictDomainsChange}
            onLabel={intl.msg("yes")}
            offLabel={intl.msg("no")}
          />
          <InfoBlock>
            {
              restrictDomains &&
                <InfoText validate="valid">{intl.msg("account_restrict_domains_helptext_valid")}</InfoText>
            }
          </InfoBlock>
        </FormGroup.Controls>
      </FormGroup>

      {
        restrictDomains &&
          <>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_allowedDomains" text={intl.msg("account_allowed_domains")} description={intl.msg("account_allowed_domains_description")}/>
              <FormGroup.Controls>
                <div className="input-group">
                  <TagInput
                    id="inp_allowedDomains"
                    className="react-select w-100"
                    classNamePrefix="react-select"
                    value={data?.allowedDomains?.map(d => {
                      return {label: d.trim(), value: d.trim()}
                    })}
                    isDisabled={!restrictDomains}
                    breakKeys={["Enter", " ", ","]}
                    lowerCase={true}
                    onChange={handleAllowedDomainsChange}
                  />
                </div>
                <InfoBlock>
                  <InfoText validate="valid">{intl.msg("account_domains_helptext_valid")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_disallowedDomains" text={intl.msg("account_disallowed_domains")} description={intl.msg("account_disallowed_domains_description")}/>
              <FormGroup.Controls>
                <div className="input-group">
                  <TagInput
                    id="inp_disallowedDomains"
                    className="react-select w-100"
                    classNamePrefix="react-select"
                    value={data?.disallowedDomains?.map(d => {
                      return {label: d.trim(), value: d.trim()}
                    })}
                    isDisabled={!restrictDomains}
                    breakKeys={["Enter", " ", ","]}
                    lowerCase={true}
                    onChange={handleDisallowedDomainsChange}
                  />
                </div>
                <InfoBlock>
                  <InfoText validate="valid">{intl.msg("account_domains_helptext_valid")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

          </>
      }

    </FormGroups>
  )

}

export function AccountSystemSettingsComps({data, placeholder, update, hidden}) {

  const {session, api, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const [amobeeBehaviorSegmentSet, setAmobeeBehaviorSegmentSet, amobeeBehaviorSegmentSetRef] = useStateRef(undefined)

  useEffect(() => {
    if (data) {
      if (data.props?.adSystem?.AMOBEE?.behavioralSegmentSetId) {
        api.getAmobeeBehaviorSegmentSet(data.props?.adSystem?.AMOBEE?.behavioralSegmentSetId)
          .then(({data: d}) => {
            setAmobeeBehaviorSegmentSet({
              value: d.behaviorSegmentSetId,
              label: d.behaviorSegmentSetName,
              popDampFactor: d.popDampFactor,
            })
          })
          .catch((error) => {
            // log.debug("Error", error)
          })
      }
    }
  }, [data])

  function handleCpmsChange(cpmType, v) {
    update({path: `props.adSystem.AMOBEE.cpms.${cpmType}`, value: v})
  }

  function handleFlagsChange(v) {
    update({path: "flags", value: v?.map(v => v.value)})
  }

  function handleAmobeeBehaviorSegmentSetChange(v) {
    setAmobeeBehaviorSegmentSet(v)
    // setTouched(true)
    update({path: `props.adSystem.AMOBEE.behavioralSegmentSetId`, value: v.value})
  }

  // async function getAmobeeBehaviorSegmentSet() {
  //   if (amobeeBehaviorSegmentSetRef.current !== undefined) {
  //     return Promise.resolve(amobeeBehaviorSegmentSetRef.current)
  //   }
  //   else if (data?.props?.adSystem?.AMOBEE?.behavioralSegmentSetId) {
  //     return api.getAmobeeBehaviorSegmentSet(data.props.adSystem.AMOBEE.behavioralSegmentSetId)
  //       .then(({d}) => {
  //         const v = {
  //           value: d.behaviorSegmentSetId,
  //           label: d.behaviorSegmentSetName,
  //           popDampFactor: d.popDampFactor,
  //         }
  //         setAmobeeBehaviorSegmentSet(v)
  //         return v
  //       })
  //       .catch((error) => {
  //         // log.debug("Error", error)
  //       })
  //   }
  // }

  function loadFlags(search, loadedOptions, additional) {

    // log.debug("loadFlags:", search, loadedOptions, additional)

    return api.listFlags({extraParams: mkExtraParams({filterParams:{flagId: search}}, {sortParams: {flagId: "ASC"}}), cursor: additional?.cursor})
      .then(({data, nextCursor}) => {
        // log.debug("loadFlags: data=", data)
        let opt = data.map(c => {
          return {
            value: c.flagId,
            label: c.flagId,
          };
        });
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        // log.debug("loadFlags: res=", res)
        return res
      });
  }

  function loadAmobeeBehaviorSegmentSets(search, loadedOptions, options) {

    // log.debug("loadAmobeeBehaviorSegmentSets:", search, loadedOptions, additional)

    return api.listAmobeeBehaviorSegmentSets({extraParams: mkExtraParams({filterParams:{behaviorSegmentSetName: search}}, {sortParams: {behaviorSegmentSetName: "ASC"}}), cursor: options?.cursor})
      .then(({data, nextCursor}) => {
        // log.debug("loadAmobeeBehaviorSegmentSets: data=", data)
        let opt = data.map(c => {
          return {
            value: c.behaviorSegmentSetId,
            label: c.behaviorSegmentSetName,
            popDampFactor: c.popDampFactor,
          };
        });
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        // log.debug("loadAmobeeBehaviorSegmentSets: res=", res)
        return res
      });
  }

  function flagsToOpts() {
    return data?.flags?.map(d => {
      return {label: d.trim(), value: d.trim()}
    })
  }

  return (

    <FormGroups id="sec_system" title={intl.msg("account_system_settings_title")} description={intl.msg("account_system_settings_description")} hidden={hidden?.sec_system} collapsed={true}>

      <FormGroup hidden={hidden?.inp_flags}>
        <FormGroup.Label htmlFor="inp_flags" text={intl.msg("account_system_flags")}  description={intl.msg("account_system_flags_description")}/>
        <FormGroup.Controls>
          <AsyncPaginate
            id="inp_flags"
            className="react-select"
            classNamePrefix="react-select"
            cacheOptions
            isMulti
            isClearable
            value={flagsToOpts()}
            debounceTimeout={800}
            hideSelectedOptions={true}
            menuPortalTarget={document.body}
            defaultOptions={true}
            loadOptions={loadFlags}
            placeholder={null}
            onChange={handleFlagsChange}
          />
          <InfoBlock>
          </InfoBlock>
        </FormGroup.Controls>
      </FormGroup>

      <FormGroup>
        <FormGroup.Label htmlFor="inp_cpms" text={intl.msg("account_adsystem_cpms")} description={intl.msg("account_adsystem_cpms_description")}/>
        <FormGroup.Controls>
          <table border="0">
            <tr>
              <td>
                <label className="col-form-label pr-2" htmlFor="inp_cpmSolv">
                  {intl.msg("account_adsystem_cpm_solv")}:
                </label>
              </td>
              <td>
                <div className="form-control-wrapper">
                  <div style={{width: "80px"}}>
                    <NumericInput id="inp_cpmSolv" className="form-control" min={null} max={90} decimals={2} value={data?.props?.adSystem?.AMOBEE?.cpms?.SOLV} placeholder={placeholder?.effProps?.adSystem?.AMOBEE?.cpms?.SOLV || intl.msg("default")} onChange={(v) => handleCpmsChange("SOLV", v)}/>
                  </div>
                  <div className="form-control-suffix" style={{paddingInline: "10px"}}>%</div>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <label className="col-form-label pr-2" htmlFor="inp_cpmImage">
                  {intl.msg("account_adsystem_cpm_image")}:
                </label>
              </td>
              <td>
                <div className="form-control-wrapper">
                  <div style={{width: "80px"}}>
                    <NumericInput id="inp_cpmImage" className="form-control" min={null} max={90} decimals={2} value={data?.props?.adSystem?.AMOBEE?.cpms?.IMAGE} placeholder={placeholder?.effProps?.adSystem?.AMOBEE?.cpms?.IMAGE || intl.msg("default")} onChange={(v) => handleCpmsChange("IMAGE", v)}/>
                  </div>
                  <div className="form-control-suffix" style={{paddingInline: "10px"}}>%</div>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <label className="col-form-label pr-2" htmlFor="inp_cpmVide">
                  {intl.msg("account_adsystem_cpm_video")}:
                </label>
              </td>
              <td>
                <div className="form-control-wrapper">
                  <div style={{width: "80px"}}>
                    <NumericInput id="inp_cpmVideo" className="form-control" min={null} max={90} decimals={2} value={data?.props?.adSystem?.AMOBEE?.cpms?.VIDEO} placeholder={placeholder?.effProps?.adSystem?.AMOBEE?.cpms?.VIDEO || intl.msg("default")} onChange={(v) => handleCpmsChange("VIDEO", v)}/>
                  </div>
                  <div className="form-control-suffix" style={{paddingInline: "10px"}}>%</div>
                </div>
              </td>
            </tr>
          </table>
          <InfoBlock>
            <InfoText validate="valid">{intl.msg("account_adsystem_cpms_helptext_valid", {minValue: data.commissionRates && data.commissionRates.length > 0 ? data.commissionRates.length : 2, maxValue: 5})}</InfoText>
            <InfoText>{intl.msg("account_adsystem_cpms_helptext_default", {minValue: data.commissionRates && data.commissionRates.length > 0 ? data.commissionRates.length : 2, maxValue: 5})}</InfoText>
            <InfoText validate="optional">{intl.msg("helptext_optional")}</InfoText>
          </InfoBlock>
        </FormGroup.Controls>
      </FormGroup>

      <FormGroup>
        <FormGroup.Label htmlFor="inp_behaviorSegmentSet" text={intl.msg("account_adsystem_amobee_behavior_segment_set")} description={intl.msg("account_adsystem_amobee_behavior_segment_set_description")}/>
        <FormGroup.Controls>
          <div style={{width: "500px"}}>
            <AsyncPaginate
              id="inp_behaviorSegmentSet"
              className="react-select"
              classNamePrefix="react-select"
              cacheOptions
              value={amobeeBehaviorSegmentSet}
              debounceTimeout={800}
              defaultOptions={true}
              isClearable={true}
              placeholder={""}
              // hideSelectedOptions={true}
              // menuPortalTarget={document.body}
              // menuPosition={"fixed"}
              loadOptions={loadAmobeeBehaviorSegmentSets}
              onChange={handleAmobeeBehaviorSegmentSetChange}
            />
          </div>
          <InfoBlock>
          </InfoBlock>
        </FormGroup.Controls>
      </FormGroup>

    </FormGroups>

  )
}

